import { useCallback, useEffect, useMemo, useState } from 'react';

import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import { useDebounce, useUpdateEffect, useDeepCompareMemoize } from '@hooks';

// import { useMediaQuery } from '@hooks/useMediaQuery';
// import { xSmallScreen } from '@styles/constants';

import { useQueryStates, queryTypes } from '@hooks/useQueryState';

import { FormProvider, useForm } from 'react-hook-form';

import dynamic from 'next/dynamic';

// import { ProductConfigurator } from '@components/organisms/ProductConfigurator';
import { ProductGallery } from '@components/organisms/ProductGallery';
import { ProductImage } from '@components/organisms/ProductImage';
import { AddToCartSection } from '@components/organisms/AddToCartSection';

import { ErrorBoundary } from '@components/containers/ErrorBoundary';

import NiceModal from '@ebay/nice-modal-react';
import { CartModal } from '@components/overlays/Cart';

import { translate } from '@utils/translations';

import { useVariantId } from './hooks/useVariantId';
import { ProductConfiguratorBody } from './ProductConfiguratorBody';
import { ProductStandardBody } from './ProductStandardBody';

import * as S from './styles';

import {
  getDefaultValues,
  getProductAttributes,
  getSelectedProductAttributes,
} from './utils';

import {
  CONFIGURATOR_PRODUCT_TYPES,
  IPageProps,
  SAMPLE_PRODUCT_TYPES,
} from './types';

const ProductConfigurator = dynamic(
  () =>
    import('../../@next/components/organisms/ProductConfigurator').then(
      (module) => module.ProductConfigurator
    ),
  {
    ssr: false,
  }
);

const Page: React.FC<IPageProps> = ({
  product,
  productDetails,
  addToCart,
  items,
}) => {
  // const [isXSmallScreen] = useMediaQuery(`(max-width: ${xSmallScreen}px)`);
  const isConfiguratorType = CONFIGURATOR_PRODUCT_TYPES.includes(
    product.productType.slug
  );
  const isSampleType = SAMPLE_PRODUCT_TYPES.includes(product.productType.slug);
  const isStandardType = !(isSampleType || isConfiguratorType);

  const [activeImage, setActiveImage] = useState<string | null>(null);

  const attributes = useMemo(() => getProductAttributes(product), [product]);

  const [query, setQuery] = useQueryStates(
    attributes.reduce(
      (acc, attr) => ({
        ...acc,
        // able to chose multiple values for samples
        [attr.attribute.slug]: isSampleType
          ? queryTypes.array(queryTypes.string).withDefault([])
          : queryTypes.string,
      }),
      {}
    )
  );

  const formConfig = useMemo(() => {
    const attributesSlugs = attributes.map((attr) => attr.attribute.slug);
    const defaultValues = pick(
      getDefaultValues(product, pickBy(query)),
      attributesSlugs
    );
    return {
      defaultValues,
    };
  }, [attributes, product, query]);

  const formMethods = useForm(formConfig);
  const formAttributes = useDeepCompareMemoize(formMethods.watch());

  useEffect(() => {
    setQuery(pickBy(formAttributes));
  }, [formAttributes]);

  const { variantId, variantStock, variantPricing } = useVariantId(
    formAttributes,
    product
  );
  const selectedProductAttributes = useMemo(
    () => getSelectedProductAttributes(formAttributes, product.attributes),
    [formAttributes, product.attributes]
  );
  const debouncedAttributes = useDebounce(formAttributes, 300);

  const handleAddToCart = useCallback(
    async (quantity: number): Promise<void> => {
      // @ analytics
      const selectedVariant = product.variants.find((o) => o?.id === variantId);
      window?.rudderanalytics?.track('Product Added', {
        currency: selectedVariant?.pricing?.price?.gross?.currency,
        name: product.productType?.slug,
        price: selectedVariant?.pricing?.price?.gross?.amount,
        product_id: product.slug,
        order_quantity: quantity,
        quantity,
        variant: variantId,
        value: selectedVariant?.pricing?.price?.gross?.amount, // TODO: remove (Pinterrest)
        total: selectedVariant?.pricing?.price?.gross?.amount, // TODO: remove (GA4)
      });
      await addToCart(variantId, quantity, selectedProductAttributes);
      NiceModal.show(CartModal);
    },
    [addToCart, product, selectedProductAttributes, variantId]
  );

  useEffect(() => {
    // console.log('Page - product viewed');
    // @ analytics
    window?.rudderanalytics?.track('Product Viewed', {
      currency: product.pricing.priceRange.start.gross.currency,
      name: product.productType.slug,
      price: product.pricing.priceRange.start.gross.amount,
      product_id: product.slug,
    });
  }, []);

  useUpdateEffect(() => {
    // Ensure that we track the second show_variation with variantId defined
    // console.log('Page - show_variation');
    // console.log(debouncedAttributes)
    // @ analytics
    const selectedVariant = product.variants.find((o) => o?.id === variantId);
    window?.rudderanalytics?.track('show_variation_new', {
      currency: product.pricing.priceRange.start.gross.currency,
      name: product.productType?.slug,
      price: selectedVariant?.pricing?.price?.gross?.amount,
      product_id: product.slug,
      variant: variantId,
    });
  }, [debouncedAttributes, product.id]);

  /*
  const getImages = () => {
    if (product.variants && variantId) {
      const variant = product.variants.find(
        (variant) => variant.id === variantId
      );

      if (variant.images.length > 0) {
        return variant.images;
      }
    }

    return product.images;
  };
  */

  const getActiveImage = () => {
    if (activeImage) {
      return { url: activeImage };
    }
    return product.images[0];
  };

  const productViewSection = (
    <ErrorBoundary>
      {isConfiguratorType && (
        <ProductConfigurator
          productType={product.productType.slug}
          attributes={attributes}
          formAttributes={formAttributes}
          // isMobile={isXSmallScreen}
        />
      )}
      {isSampleType && <ProductImage image={getActiveImage()} />}
      {isStandardType && <ProductGallery productSlug={product.slug} />}
    </ErrorBoundary>
  );

  const addToCartSection = (
    <AddToCartSection
      items={items}
      product={product}
      attributes={attributes}
      formAttributes={formAttributes}
      productId={product.id}
      productName={
        isConfiguratorType
          ? product.productType.name
          : translate(product, 'name')
      }
      variantId={variantId}
      productAttributes={selectedProductAttributes}
      isSampleType={isSampleType}
      onAddToCart={handleAddToCart}
      setActiveImage={setActiveImage}
      productPricing={product.pricing}
      variantPricing={variantPricing}
      variantStock={variantStock}
    />
  );

  const productBodySection = (
    <div className="product-body">
      {isConfiguratorType && (
        <ProductConfiguratorBody
          product={product}
          productDetails={productDetails}
        />
      )}
      {isSampleType && <ProductStandardBody productDetails={productDetails} />}
      {isStandardType && (
        <ProductStandardBody productDetails={productDetails} />
      )}
    </div>
  );

  return (
    <S.Wrapper className="product-page">
      <S.ProductDetails className="product-details">
        <FormProvider {...formMethods}>
          <S.ProductViewWr className="product-details_product-view-wr">
            {productViewSection}
          </S.ProductViewWr>
          <S.ProductControlsWr className="product-details__controls-wr">
            {addToCartSection}
          </S.ProductControlsWr>
        </FormProvider>
      </S.ProductDetails>
      {productBodySection}
    </S.Wrapper>
  );
};

Page.displayName = 'ProductPage';

export default Page;
